import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import BranchLink from '@/components/BranchLink';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, ArrowRight, Banknote } from 'lucide-react';
import api from '@/lib/api';
import { useBranchStore } from '@/stores';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { BranchFilter } from '@/components/BranchFilter';
import { DateFilter } from '@/components/DateFilter';
import { useLanguage } from '@/contexts/LanguageContext';
import { PersonPicker } from '@/components/common/PersonPicker';
import { localToday } from '@/lib/dateRange';
import { CashCustodyPanel } from '@/components/transfers/CashCustodyPanel';

// A bank account is selectable for a branch if it's shared with that branch
// (branch_ids list), directly assigned (branch_id), or unscoped (shown for all).
// "office" / no branch selected shows every account.
const accountVisibleForBranch = (acc, branchId) => {
  if (!branchId) return true;
  const ids = (acc.branch_ids && acc.branch_ids.length)
    ? acc.branch_ids
    : (acc.branch_id ? [acc.branch_id] : []);
  if (ids.length === 0) return true;
  return ids.includes(branchId);
};

export default function CashTransfersPage() {
  const { t } = useLanguage();
  const [transfers, setTransfers] = useState([]);
  const { branches, fetchBranches: _fetchBr } = useBranchStore();
  const [employees, setEmployees] = useState([]);
  const [partners, setPartners] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [branchFilter, setBranchFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState({ start: null, end: null, period: 'all' });
  const [formData, setFormData] = useState({ from_branch_id: '', to_branch_id: '', amount: '', transfer_mode: 'cash', from_mode: 'cash', to_mode: 'cash', from_bank_account_id: '', to_bank_account_id: '', sender_name: '', receiver_name: '', date: localToday(), notes: '', purpose: 'funding' });

  useEffect(() => { fetchData(); }, []);

  // Exact-record highlight: scroll to + pulse a transfer row when arriving
  // with ?focus=<id> (reuses the shared row-focus-pulse pattern).
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFocus = searchParams.get('focus');
  useEffect(() => {
    if (!urlFocus || !transfers?.length) return;
    if (!transfers.some(t => t.id === urlFocus)) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-testid="transfer-row-${urlFocus}"]`);
      if (el) {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch { /* noop */ }
        el.classList.add('row-focus-pulse');
        setTimeout(() => {
          el.classList.remove('row-focus-pulse');
          const sp = new URLSearchParams(searchParams);
          sp.delete('focus');
          setSearchParams(sp, { replace: true });
        }, 2800);
      }
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFocus, transfers]);

  const fetchData = async () => {
    try {
      const [tRes, eRes, pRes, bRes] = await Promise.all([
        api.get('/cash-transfers?limit=5000'),
        api.get('/employees'),
        api.get('/partners').catch(() => ({ data: [] })),
        api.get('/bank-accounts?active_only=true').catch(() => ({ data: [] })),
      ]);
      setTransfers(tRes.data?.data || tRes.data || []);
      setEmployees(eRes.data?.data || eRes.data || []);
      setPartners(pRes.data?.data || pRes.data || []);
      setBankAccounts(bRes.data?.data || bRes.data || []);
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Mixed Cash↔Bank: each bank leg requires its own account so the
    // backend can move that balance and post the paired journal entry.
    if (formData.from_mode === 'bank' && !formData.from_bank_account_id) {
      toast.error('Please pick the sender bank account'); return;
    }
    if (formData.to_mode === 'bank' && !formData.to_bank_account_id) {
      toast.error('Please pick the receiver bank account'); return;
    }
    try {
      await api.post('/cash-transfers', {
        ...formData,
        amount: parseFloat(formData.amount),
        // transfer_mode kept for legacy display: same-type → that type, else 'mixed'
        transfer_mode: formData.from_mode === formData.to_mode ? formData.from_mode : 'mixed',
        from_mode: formData.from_mode,
        to_mode: formData.to_mode,
        from_branch_id: formData.from_branch_id || null,
        to_branch_id: formData.to_branch_id || null,
        from_bank_account_id: formData.from_mode === 'bank' ? formData.from_bank_account_id || null : null,
        to_bank_account_id: formData.to_mode === 'bank' ? formData.to_bank_account_id || null : null,
        date: `${formData.date}T${new Date().toTimeString().slice(0,8)}`,
      });
      toast.success('Transfer recorded');
      setShowDialog(false);
      setFormData({ from_branch_id: '', to_branch_id: '', amount: '', transfer_mode: 'cash', from_mode: 'cash', to_mode: 'cash', from_bank_account_id: '', to_bank_account_id: '', sender_name: '', receiver_name: '', date: localToday(), notes: '', purpose: 'funding' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAck = async (id) => {
    try { await api.post(`/cash-transfers/${id}/acknowledge`, {}); toast.success('Receipt confirmed'); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this transfer?')) {
      try { await api.delete(`/cash-transfers/${id}`); toast.success('Deleted'); fetchData(); }
      catch { toast.error('Failed'); }
    }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>;

  const filtered = transfers.filter(t => {
    if (branchFilter.length > 0 && !branchFilter.includes(t.from_branch_id) && !branchFilter.includes(t.to_branch_id)) return false;
    if (dateFilter.start && dateFilter.end) { const d = new Date(t.date); return d >= new Date(dateFilter.start) && d <= new Date(dateFilter.end + 'T23:59:59'); }
    return true;
  });
  const totalTransferred = filtered.reduce((s, t) => s + t.amount, 0);
  
  // Company balance: incoming from branches - outgoing to branches
  const companyIncoming = transfers.filter(t => !t.to_branch_id && t.from_branch_id).reduce((s, t) => s + t.amount, 0);
  const companyOutgoing = transfers.filter(t => t.to_branch_id && !t.from_branch_id).reduce((s, t) => s + t.amount, 0);
  const companyCash = transfers.filter(t => !t.to_branch_id && t.from_branch_id && t.transfer_mode === 'cash').reduce((s, t) => s + t.amount, 0) - transfers.filter(t => t.to_branch_id && !t.from_branch_id && t.transfer_mode === 'cash').reduce((s, t) => s + t.amount, 0);
  const companyBank = transfers.filter(t => !t.to_branch_id && t.from_branch_id && t.transfer_mode === 'bank').reduce((s, t) => s + t.amount, 0) - transfers.filter(t => t.to_branch_id && !t.from_branch_id && t.transfer_mode === 'bank').reduce((s, t) => s + t.amount, 0);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-200/50">

                <Banknote size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="cash-transfers-title">Money Transfers</h1>

                <p className="text-sm text-muted-foreground mt-0.5">Move money (cash or bank) between branches, office & accounts</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Moving stock or goods?{' '}
                  <Link to="/transfers" className="text-primary font-medium hover:underline" data-testid="goto-stock-transfers-link">Go to Stock Transfers →</Link>
                </p>

              </div>

            </div>
            <p className="text-muted-foreground">Track cash & bank movements between branches and office</p>
          </div>
          <div className="flex gap-3 items-center flex-wrap">
            <BranchFilter onChange={setBranchFilter} />
            <DateFilter onFilterChange={setDateFilter} />
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild><Button className="rounded-full" data-testid="add-transfer-btn"><Plus size={18} className="mr-2" />New Transfer</Button></DialogTrigger>
              <DialogContent data-testid="transfer-dialog">
                <DialogHeader><DialogTitle className="font-outfit">Record Cash Transfer</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>From</Label>
                      <Select value={formData.from_branch_id || "office"} onValueChange={(v) => setFormData({ ...formData, from_branch_id: v === "office" ? "" : v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="office">Company / Head Office</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>To</Label>
                      <Select value={formData.to_branch_id || "office"} onValueChange={(v) => setFormData({ ...formData, to_branch_id: v === "office" ? "" : v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="office">Company / Head Office</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Amount *</Label><Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required data-testid="transfer-amount" /></div>
                    <div><Label>From (fund type)</Label>
                      <Select value={formData.from_mode} onValueChange={(v) => setFormData({ ...formData, from_mode: v, from_bank_account_id: v === 'cash' ? '' : formData.from_bank_account_id })}>
                        <SelectTrigger data-testid="from-mode-select"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div><Label>To (fund type)</Label>
                      <Select value={formData.to_mode} onValueChange={(v) => setFormData({ ...formData, to_mode: v, to_bank_account_id: v === 'cash' ? '' : formData.to_bank_account_id })}>
                        <SelectTrigger data-testid="to-mode-select"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank</SelectItem></SelectContent>
                      </Select>
                    </div>
                    {formData.from_mode !== formData.to_mode && (
                      <div className="sm:col-span-2 text-[11px] text-info bg-info/5 border border-info/30 rounded-lg px-2 py-1.5" data-testid="mixed-transfer-hint">
                        Mixed transfer ({formData.from_mode} → {formData.to_mode}) — pure asset move: no expense, no income, no P&L effect.
                      </div>
                    )}
                    {/* Per-leg bank account pickers — shown only for a bank leg
                        so we can $inc that bank_account balance atomically. */}
                    {(formData.from_mode === 'bank' || formData.to_mode === 'bank') && (
                      <>
                        <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {formData.from_mode === 'bank' && (
                          <div>
                            <Label>From Bank Account *</Label>
                            <Select value={formData.from_bank_account_id || ''} onValueChange={(v) => setFormData({ ...formData, from_bank_account_id: v })}>
                              <SelectTrigger data-testid="from-bank-account"><SelectValue placeholder="Sender's bank account" /></SelectTrigger>
                              <SelectContent>
                                {bankAccounts.filter((b) => accountVisibleForBranch(b, formData.from_branch_id)).map((b) => (
                                  <SelectItem key={b.id} value={b.id}>
                                    {b.bank_name || b.name} · {(b.account_number || '').slice(-4).padStart(4, '•')}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          )}
                          {formData.to_mode === 'bank' && (
                          <div>
                            <Label>To Bank Account *</Label>
                            <Select value={formData.to_bank_account_id || ''} onValueChange={(v) => setFormData({ ...formData, to_bank_account_id: v })}>
                              <SelectTrigger data-testid="to-bank-account"><SelectValue placeholder="Receiver's bank account" /></SelectTrigger>
                              <SelectContent>
                                {bankAccounts.filter((b) => accountVisibleForBranch(b, formData.to_branch_id)).map((b) => (
                                  <SelectItem key={b.id} value={b.id}>
                                    {b.bank_name || b.name} · {(b.account_number || '').slice(-4).padStart(4, '•')}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          )}
                        </div>
                      </>
                    )}
                    <div>
                      <Label>Sender Name *</Label>
                      <PersonPicker
                        value={formData.sender_name}
                        onChange={(v) => setFormData({ ...formData, sender_name: v })}
                        employees={employees}
                        partners={partners}
                        placeholder="Pick employee/partner or type a name"
                        testId="sender-name"
                      />
                    </div>
                    <div>
                      <Label>Receiver Name *</Label>
                      <PersonPicker
                        value={formData.receiver_name}
                        onChange={(v) => setFormData({ ...formData, receiver_name: v })}
                        employees={employees}
                        partners={partners}
                        placeholder="Pick employee/partner or type a name"
                        testId="receiver-name"
                      />
                    </div>
                    <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} /></div>
                    <div><Label>Purpose</Label>
                      <Select value={formData.purpose} onValueChange={(v) => setFormData({ ...formData, purpose: v })}>
                        <SelectTrigger data-testid="transfer-purpose"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="funding">Funding (branch owes the sender)</SelectItem>
                          <SelectItem value="settlement">Settles an inter-branch balance</SelectItem>
                          <SelectItem value="deposit_collection">Cash collected for bank deposit</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2 text-[11px] text-muted-foreground" data-testid="transfer-vs-handover-hint">Money really moving to another branch / office. Cash given to a person to hold for the branch → use “Cash handover” below.</div>
                    <div><Label>Notes</Label><Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Optional" /></div>
                  </div>
                  <div className="flex gap-3">
                    <Button type="submit" className="rounded-full" data-testid="submit-transfer">Record Transfer</Button>
                    <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="rounded-full">Cancel</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card className="border-border border-primary/30 bg-gradient-to-r from-primary/5 to-amber-50">
          <CardHeader className="pb-2"><CardTitle className="font-outfit text-base">Company / Head Office Balance</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-white rounded-xl text-center"><div className="text-xs text-muted-foreground">From Branches</div><div className="text-lg font-bold text-success">{cur()} {companyIncoming.toFixed(2)}</div></div>
              <div className="p-3 bg-white rounded-xl text-center"><div className="text-xs text-muted-foreground">To Branches</div><div className="text-lg font-bold text-error">{cur()} {companyOutgoing.toFixed(2)}</div></div>
              <div className="p-3 bg-cash/10 rounded-xl text-center border border-cash/20"><div className="text-xs text-muted-foreground">Cash at Company</div><div className="text-lg font-bold text-cash">{cur()} {companyCash.toFixed(2)}</div></div>
              <div className="p-3 bg-bank/10 rounded-xl text-center border border-bank/20"><div className="text-xs text-muted-foreground">Bank at Company</div><div className="text-lg font-bold text-bank">{cur()} {companyBank.toFixed(2)}</div></div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Move cash/bank from branches to Company or Company to branches using "New Transfer"</p>
          </CardContent>
        </Card>

        <CashCustodyPanel branches={branches} bankAccounts={bankAccounts} />

        <Card className="border-border"><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Total Transferred</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold font-outfit text-primary"> {cur()} {totalTransferred.toFixed(2)}</div></CardContent></Card>

        <Card className="border-border">
          <CardHeader><CardTitle className="font-outfit">All Transfers</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="transfers-table">
                <thead><tr className="border-b border-border">
                  <th className="text-left p-3 font-medium text-sm">Date</th>
                  <th className="text-left p-3 font-medium text-sm">From</th>
                  <th className="text-center p-3 font-medium text-sm"></th>
                  <th className="text-left p-3 font-medium text-sm">To</th>
                  <th className="text-right p-3 font-medium text-sm">Amount</th>
                  <th className="text-left p-3 font-medium text-sm hidden sm:table-cell">Mode</th>
                  <th className="text-left p-3 font-medium text-sm hidden md:table-cell">Sender</th>
                  <th className="text-left p-3 font-medium text-sm hidden md:table-cell">Receiver</th>
                  <th className="text-left p-3 font-medium text-sm hidden lg:table-cell">Notes</th>
                  <th className="text-left p-3 font-medium text-sm">Receipt</th>
                  <th className="text-right p-3 font-medium text-sm">Actions</th>
                </tr></thead>
                <tbody>
                  {filtered.map(t => (
                    <tr key={t.id} className="border-b border-border hover:bg-secondary/50" data-testid={`transfer-row-${t.id}`}>
                      <td className="p-3 text-sm">{format(new Date(t.date), 'MMM dd, yyyy')}</td>
                      <td className="p-3 text-sm font-medium">
                        {t.from_branch_id ? <BranchLink branchId={t.from_branch_id} name={t.from_branch_name || 'Office'} /> : (t.from_branch_name || 'Office')}
                      </td>
                      <td className="p-3 text-center"><ArrowRight size={16} className="text-primary mx-auto" /></td>
                      <td className="p-3 text-sm font-medium">
                        {t.to_branch_id ? <BranchLink branchId={t.to_branch_id} name={t.to_branch_name || 'Office'} /> : (t.to_branch_name || 'Office')}
                      </td>
                      <td className="p-3 text-sm text-right font-bold"> {cur()} {t.amount.toFixed(2)}</td>
                      <td className="p-3 hidden sm:table-cell"><Badge variant="secondary" className="capitalize">{t.transfer_mode}</Badge></td>
                      <td className="p-3 text-sm hidden md:table-cell">{t.sender_name}</td>
                      <td className="p-3 text-sm hidden md:table-cell">{t.receiver_name}</td>
                      <td className="p-3 text-sm text-muted-foreground hidden lg:table-cell">{t.notes || '-'}</td>
                      <td className="p-3 text-sm" data-testid={`transfer-receipt-${t.id}`}>
                        {t.receipt_status === 'received'
                          ? <Badge className="bg-emerald-100 text-emerald-800" title={t.received_by_name}>Received</Badge>
                          : t.receipt_status === 'pending'
                            ? <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleAck(t.id)} data-testid={`transfer-ack-${t.id}`}>Confirm receipt</Button>
                            : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="p-3 text-right"><Button size="sm" variant="outline" onClick={() => handleDelete(t.id)} className="h-8 text-error hover:text-error"><Trash2 size={14} /></Button></td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={11} className="p-8 text-center text-muted-foreground">No transfers yet</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
